import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import { SocketService, SocketServerEvent } from '../../../services/socket.service';
import { ServerTimeService } from '../../../services/server-time';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue, resolvePeriodForBlockLabel } from './log-sheet-report.data';
import {
  applyPreviousValueMode,
  buildReportBlockGroups,
  commitReportAutocompleteValue,
  buildEditDataFormValue,
  clearTruthyFormControls,
  downloadReportBlob,
  extractReportBsCalendar,
  extractReportDateConversion,
  extractReportResponseValue,
  extractReportStringValue,
  filterReportOptions,
  findReportOptionByLabel,
  buildGroupedReportHierarchyNodes,
  buildLinkedReportHierarchyNodes,
  flattenReportHierarchyNodes,
  formatReportNumber,
  getReportArrayValue,
  getReportCellText,
  getNullableNumberValue,
  getReportDateLabel,
  getReportHierarchyNodeOrderKey,
  getReportRowBlockExtremeValue,
  getReportRecord,
  getReportRecordValue,
  handleReportAutocompleteSearch,
  hasTextValue,
  isLogsheetDataDetailCell,
  normalizeReportNumber,
  isOutsideElement,
  shouldFetchPreviousValue,
  toggleReportHierarchyRow,
} from '../shared/report-ui.helpers';
import {
  LogSheetDateConversionResponse,
  LogSheetBlockCell,
  LogSheetBlockDefinition,
  LogSheetBsCalendarCell,
  LogSheetBsCalendarResponse,
  LogSheetPreviousBlockValueResponse,
  LogSheetReportResponse,
  LogSheetRow,
  LogSheetSummary,
  LogSheetSubstationOption,
  LogSheetTimeIntervalOption,
} from './log-sheet-report.interfaces';

type EditableCellKey = `${number}-${number}`;

interface LogSheetHierarchyNode {
  row: LogSheetRow;
  children: LogSheetHierarchyNode[];
}

interface LogSheetBlockGroup {
  period: LogSheetBlockDefinition['period'];
  label: string;
  span: number;
}

type LogSheetViewTab = 'generation' | 'summary';
type LogSheetDisplayMode = 'generation' | 'spill' | 'water-level' | 'frequency-voltage' | 'interruption' | 'summary';

const FIRST_COLUMN_HEADER_LABELS: Record<LogSheetDisplayMode, string> = {
  generation: 'Parameter / Hour',
  interruption: 'Parameter / Hour',
  spill: 'Generator / Hour',
  'water-level': 'Generator / Hour',
  'frequency-voltage': 'Substation / Hour',
  summary: 'Source / Hour',
};
const POWER_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-kabeli-corridor',
  'row:power-kathmandu-load',
] as const;
const SUMMARY_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-total-import',
  'row:power-total-export',
  'row:power-net-import',
  'row:power-system-load-actual',
  'row:power-interruption-tripping',
  'row:power-system-load-expected',
  'row:power-national-load-actual',
  'row:power-frequency-voltage-profile',
  'row:power-frequency-voltage-substation',
  'row:power-kabeli-corridor',
  'row:power-kathmandu-load',
  'row:power-spill',
  'row:power-water-level',
] as const;
const POWER_ACCORDION_ORDER_MAP = new Map<string, number>(
  POWER_ACCORDION_ORDER.map((key, index) => [key, index]),
);
const SUMMARY_ACCORDION_ORDER_MAP = new Map<string, number>(
  SUMMARY_ACCORDION_ORDER.map((key, index) => [key, index]),
);

@Component({
  selector: 'app-log-sheet-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './log-sheet-report.html',
  styleUrl: './log-sheet-report.scss',
})
export class LogSheetReportPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isSavingCell = false;
  protected selectedViewTab: LogSheetViewTab = 'generation';
  protected detailMode: LogSheetDisplayMode = 'generation';

  protected blockColumns: LogSheetBlockDefinition[] = [];
  protected blockGroups: LogSheetBlockGroup[] = [];
  protected rows: LogSheetRow[] = [];
  protected baseLoad = 0;
  protected summary: LogSheetSummary = {
    scheduled_mwh: 0,
    actual_mwh: 0,
    deviation_mwh: 0,
    row_count: 0,
  };

  protected substationOptions: LogSheetSubstationOption[] = [];
  protected filteredSubstationOptions: LogSheetSubstationOption[] = [];
  protected substationSearchTerm = '';
  protected selectedDateDisplayLabel = getTodayDateValue();
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: LogSheetBsCalendarResponse | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  protected timeIntervalOptions: LogSheetTimeIntervalOption[] = [
    { value: 60, label: 'Hourly' },
  ];

  protected editDataModalOpen = false;
  protected remarksForm: UntypedFormGroup;
  protected socketId: string | null = null;

  @ViewChild('tableWrap') private readonly tableWrap?: ElementRef<HTMLDivElement>;
  @ViewChild('hiddenDateInput') private readonly hiddenDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bsDatePickerShell') private readonly bsDatePickerShell?: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private hierarchyRoots: LogSheetHierarchyNode[] = [];
  private reportRowsCache: LogSheetRow[] = [];
  private expandedRowKeys = new Set<string>();
  private pendingViewStateRestore: {
    expandedRowKeys: string[];
    scrollTop: number;
    scrollLeft: number;
  } | null = null;
  private pendingRemarksContext: {
    station_id: number;
    station_code: string | null;
    row_key: string | null;
    row_kind: string | null;
    block_no: number;
    block_value: number | null;
    remarks: string | null;
    data_type?: 'POWER' | 'ENERGY';
  } | null = null;
  private selectedDateRequestValue = getTodayDateValue();
  private selectedDateAdValue = getTodayDateValue();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly socketSvc: SocketService,
  ) {
    this.filtersForm = this.fb.group({
      date: [getTodayDateValue(), Validators.required],
      substation_id: [null],
      show_values: [null],
      time_interval: [60],
    });
    this.remarksForm = this.fb.group({
      data_value: [''],
      copy_previous_block_value: [false],
      carry_over_previous_day_values: [false],
      remarks: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.loadSubstationOptions();
    this.listenToSocketEvents();
    this.listenToPreferenceChanges();
    await this.handleDateSelectionChange();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filtersForm.patchValue(
      {
        date: getTodayDateValue(),
        substation_id: null,
        show_values: null,
        time_interval: 60,
      },
      { emitEvent: false },
    );
    this.substationSearchTerm = '';
    this.refreshGeneratorOptionsForDetailMode(false);
    this.bsCalendarOpen = false;
    void this.handleDateSelectionChange();
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    const payload = this.buildReportPayload();
    this.isExporting = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_LOG_SHEET_REPORT, ApiMethod.POST, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName()),
      });
  }

  protected trackByBlock(_: number, item: LogSheetBlockDefinition): number {
    return item.block_no;
  }

  protected trackByBlockGroup(_: number, item: LogSheetBlockGroup): string {
    return `${item.period}-${item.span}`;
  }

  protected trackByRow(_: number, item: LogSheetRow): string {
    return item.row_key ?? `${item.row_kind ?? 'data'}-${item.station_id}`;
  }

  protected trackBySubstation(_: number, item: LogSheetSubstationOption): number {
    return item.id;
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    handleReportAutocompleteSearch({
      query: event?.query,
      form: this.filtersForm,
      controlName: 'substation_id',
      normalizeNumber: (value) => this.normalizeNumber(value),
      filterOptions: (query) => this.filterSubstationAutocomplete(query),
      findOption: (label) => this.findSubstationByLabel(label),
    });
  }

  protected onSubstationSearchBlur(): void {
    this.commitSubstationAutocompleteValue();
  }

  protected getSelectedShowValuesLabel(): string {
    const value = this.filtersForm.get('show_values')?.value;
    const label = typeof value === 'string' && value ? value : 'Select Value';
    return label;
  }

  protected getFilterDateLabel(): string {
    return getReportDateLabel(this.selectedDateDisplayLabel, this.selectedDateRequestValue, this.selectedDateAdValue);
  }

  protected getHeaderSummary(): string {
    const count = this.summary.row_count;
    const blockCount = this.blockColumns.length;
    return `Showing ${count} generator${count === 1 ? '' : 's'} • ${blockCount} generation block${blockCount === 1 ? '' : 's'}`;
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected setViewTab(viewTab: LogSheetViewTab): void {
    const shouldResetAccordionState = this.selectedViewTab !== viewTab;
    this.selectedViewTab = viewTab;
    this.pendingViewStateRestore = null;
    this.detailMode = viewTab === 'summary' ? 'summary' : 'generation';
    if (!shouldResetAccordionState || !this.reportRowsCache.length) {
      return;
    }

    const hierarchyRows = this.reportRowsCache.filter((row) => !row.force_flat);
    this.hierarchyRoots = this.buildHierarchyNodes(hierarchyRows);
    this.expandedRowKeys = this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.pruneSummaryCollapsedSourceKeys(this.hierarchyRoots, this.expandedRowKeys);
    }
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  protected handleDetailModeChange(mode: LogSheetDisplayMode): void {
    if (this.detailMode === mode) {
      return;
    }

    this.detailMode = mode;
    if (mode === 'summary') {
      this.selectedViewTab = 'generation';
      this.pendingViewStateRestore = null;
      this.refreshGeneratorOptionsForDetailMode();
      this.loadReport(this.rows.length > 0);
      return;
    }

    if (this.selectedViewTab === 'summary') {
      this.selectedViewTab = 'generation';
      this.pendingViewStateRestore = null;
    }

    this.refreshGeneratorOptionsForDetailMode();
    this.loadReport(this.rows.length > 0);
  }

  protected isDetailModeActive(mode: LogSheetDisplayMode): boolean {
    return this.detailMode === mode;
  }

  protected getCellText(cell: LogSheetBlockCell): string {
    return getReportCellText(cell);
  }

  protected getRowCellText(row: LogSheetRow, cell: LogSheetBlockCell): string {
    return this.isBlankParentRow(row) ? '' : this.getCellText(cell);
  }

  protected isEditedCell(cell: LogSheetBlockCell): boolean {
    return isLogsheetDataDetailCell(cell);
  }

  protected isAdminEditedCell(cell: LogSheetBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group === 'admin';
  }

  protected isUserEditedCell(cell: LogSheetBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group !== 'admin';
  }

  protected getTotalCellText(value: number): string {
    return this.formatNumber(value);
  }

  protected getRowMaxText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return this.formatNumber(this.getRowBlockMaxValue(row));
  }

  protected getRowMinText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return this.formatNumber(this.getRowBlockMinValue(row));
  }

  protected getRowEnergyText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return row.energy_mwh === null || row.energy_mwh === undefined ? '_' : this.formatNumber(row.energy_mwh);
  }

  protected isEnergyCellEdited(row: LogSheetRow): boolean {
    return row.energy_source === 'logsheet_data_details';
  }

  protected isEnergyCellInteractive(row: LogSheetRow): boolean {
    return (
      this.isSummaryMode() &&
      !this.isBlankParentRow(row) &&
      !this.isFrontendAggregateEnergyRow(row) &&
      this.isYesterdayDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.editDataModalOpen
    );
  }

  protected isFrontendAggregateEnergyRow(row: LogSheetRow): boolean {
    return row.row_kind === 'sector-total' || row.row_kind === 'source-total' || row.row_kind === 'subcat-total';
  }

  protected getInstalledCapacityText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return this.formatNumber(row.installed_capacity ?? 0);
  }

  protected getBaseLoadText(): string {
    return this.formatNumber(this.baseLoad);
  }

  protected getFirstColumnHeaderLabel(): string {
    return FIRST_COLUMN_HEADER_LABELS[this.detailMode];
  }

  protected getReportUnitLabel(): string {
    return this.detailMode === 'water-level' ? 'Meter (M)' : 'MW';
  }

  protected isSummaryMode(): boolean {
    return this.detailMode === 'summary';
  }

  protected getTableRows(): LogSheetRow[] {
    if (this.detailMode !== 'water-level') {
      return this.rows;
    }

    return this.rows.filter((row) => row.row_kind === 'data' || row.row_kind === 'power-water-level-generator');
  }

  protected isCellInteractive(row: LogSheetRow, cell: LogSheetBlockCell): boolean {
    return (
      this.isEditableLogsheetRow(row) &&
      this.isHourlyTimeIntervalSelected() &&
      !this.isSectorTotalRow(row) &&
      this.isCurrentDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.editDataModalOpen
    );
  }

  protected isGeneratorRow(row: LogSheetRow): boolean {
    const rowKind = row.row_kind ?? 'data';
    return rowKind === 'data' || rowKind.endsWith('-generator') || Boolean(row.is_small_power);
  }

  protected isEditableLogsheetRow(row: LogSheetRow): boolean {
    const rowKind = row.row_kind ?? 'data';
    const rowKey = row.row_key ?? '';
    return (
      this.isGeneratorRow(row) ||
      rowKind === 'interruption-line' ||
      rowKind === 'interruption-substation' ||
      rowKind === 'interruption-interruption' ||
      (this.detailMode === 'frequency-voltage' && rowKind === 'data' && rowKey.startsWith('frequency-voltage-substation-'))
    );
  }

  private isHourlyTimeIntervalSelected(): boolean {
    return (this.normalizeNumber(this.filtersForm.get('time_interval')?.value) ?? 60) === 60;
  }

  protected isSectorTotalRow(row: LogSheetRow): boolean {
    return this.isHierarchyTotalRow(row) && !this.isEditableLogsheetRow(row);
  }

  protected isHierarchyTotalRow(row: LogSheetRow): boolean {
    if (this.detailMode === 'water-level' && row.row_kind === 'power-water-level-generator') {
      return false;
    }

    return row.row_kind !== 'data';
  }

  protected hasHierarchyChildren(row: LogSheetRow): boolean {
    if (!row.row_key || !row.has_children) {
      return false;
    }

    if (this.selectedViewTab === 'summary') {
      if (row.row_kind === 'subcat-total') {
        return false;
      }
    }

    return true;
  }

  protected isBlankParentRow(row: LogSheetRow): boolean {
    return (
      row.row_kind === 'interruption-line-flows' ||
      row.row_kind === 'frequency-voltage-substation' ||
      row.row_key === 'power-frequency-voltage-profile' ||
      row.row_key === 'power-frequency-voltage-substation'
    );
  }

  protected isHierarchyExpanded(row: LogSheetRow): boolean {
    if (!row.row_key) {
      return false;
    }

    return this.expandedRowKeys.has(row.row_key);
  }

  protected toggleHierarchyRow(row: LogSheetRow): void {
    if (!toggleReportHierarchyRow(row, this.expandedRowKeys)) {
      return;
    }

    this.rows = this.buildRowsFromCurrentState();
  }

  protected beginCellEdit(row: LogSheetRow, cell: LogSheetBlockCell): void {
    if (!this.isCellInteractive(row, cell)) {
      return;
    }

    this.openEditDataModal(row, cell);
  }

  protected beginEnergyEdit(row: LogSheetRow): void {
    if (!this.isEnergyCellInteractive(row)) {
      return;
    }

    this.openEnergyEditDataModal(row);
  }

  protected closeEditDataModal(): void {
    this.editDataModalOpen = false;
    this.pendingRemarksContext = null;
    this.remarksForm.reset(
      { data_value: '', copy_previous_block_value: false, carry_over_previous_day_values: false, remarks: '' },
      { emitEvent: false },
    );
  }

  protected saveEditData(): void {
    if (!this.pendingRemarksContext) {
      this.closeEditDataModal();
      return;
    }

    const context = { ...this.pendingRemarksContext };
    const formValue = this.remarksForm.getRawValue();
    const remarks = typeof formValue.remarks === 'string' ? formValue.remarks.trim() : '';
    const dataValue = this.normalizeNullableNumber(formValue.data_value);
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: context.station_id,
      station_code: context.station_code,
      row_key: context.row_key,
      row_kind: context.row_kind,
      block_no: context.block_no,
      block_value: dataValue,
      remarks: remarks || null,
      data_type: context.data_type ?? 'POWER',
      detail_mode: this.detailMode,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    if (context.data_type === 'ENERGY') {
      this.updateLocalEnergyValue(context.row_key, dataValue, remarks || null);
    } else {
      this.updateLocalCellValue(context.station_id, context.block_no, dataValue, remarks || null);
    }
    this.isSavingCell = true;
    this.httpSvc
      .requestCall(AuthEndPoints.UPDATE_LOG_SHEET_CELL, ApiMethod.PATCH, payload, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSavingCell = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this.closeEditDataModal();
          this.loadReport(true);
        },
        error: () => {
          this.loadReport(true);
        },
      });
  }

  protected onEditDataValueInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = target?.value ?? '';
    if (hasTextValue(nextValue)) {
      this.clearPreviousValueSelections('copy_previous_block_value', 'carry_over_previous_day_values');
    }
  }

  private clearPreviousValueSelections(...controlNames: string[]): void {
    clearTruthyFormControls(this.remarksForm, controlNames);
  }

  protected onPreviousValueModeChange(mode: 'copy' | 'carry'): void {
    applyPreviousValueMode(this.remarksForm, mode);
  }

  protected arePreviousValueControlsDisabled(): boolean {
    const dataValue = this.remarksForm.get('data_value')?.value;
    return this.isEnergyEditContext() || hasTextValue(dataValue);
  }

  protected isEnergyEditContext(): boolean {
    return this.pendingRemarksContext?.data_type === 'ENERGY';
  }

  protected onCopyPreviousBlockValueChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'copy_previous_block_value', 'carry_over_previous_day_values', Boolean(context), this.isEnergyEditContext()) || !context) {
      return;
    }

    this.fetchPreviousBlockValue(context.station_id, context.block_no);
  }

  protected onCarryOverPreviousDayValuesChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'carry_over_previous_day_values', 'copy_previous_block_value', Boolean(context), this.isEnergyEditContext()) || !context) {
      return;
    }

    this.fetchPreviousDayBlockValue(context.station_id, context.block_no);
  }

  protected formatNumber(value: number | null): string {
    return formatReportNumber(value);
  }

  protected getEmptyColspan(): number {
    return this.blockColumns.length + (this.isSummaryMode() ? 5 : 1);
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    if (!this.bsCalendarOpen) {
      return;
    }

    if (!isOutsideElement(event.target, this.bsDatePickerShell?.nativeElement)) {
      return;
    }

    this.closeBsDatePicker();
  }

  private collapseAllAccordions(): void {
    this.expandedRowKeys.clear();
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private bindFilterListeners(): void {
    this.filtersForm.get('date')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleDateSelectionChange(this.rows.length > 0);
      });

    for (const controlName of ['substation_id', 'show_values', 'time_interval']) {
      this.filtersForm.get(controlName)?.valueChanges
        .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.loadReport(this.rows.length > 0));
    }

    this.remarksForm.get('copy_previous_block_value')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCopyPreviousBlockValueChange());

    this.remarksForm.get('carry_over_previous_day_values')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCarryOverPreviousDayValuesChange());
  }

  private listenToSocketEvents(): void {
    this.socketSvc.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleSocketEvent(event));
    this.socketSvc.socketId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((socketId) => {
        this.socketId = socketId;
        this.cdr.detectChanges();
      });
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        this.closeBsDatePicker();
        this.handleDateSelectionChange(this.rows.length > 0);
      });
  }

  private handleSocketEvent(event: SocketServerEvent): void {
    if (event.event === 'scada_sync_completed') {
      this.loadReport(true);
    }
  }

  private loadSubstationOptions(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '', false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.substationOptions = this.extractSubstationOptions(response);
          this.refreshGeneratorOptionsForDetailMode();
          this.cdr.detectChanges();
        },
        error: () => {
          this.substationOptions = [];
          this.filteredSubstationOptions = [];
          this.cdr.detectChanges();
        },
      });
  }

  private filterSubstationAutocomplete(query: string): void {
    const sourceOptions = this.getVisibleGeneratorOptions();
    this.filteredSubstationOptions = filterReportOptions(sourceOptions, query);
  }

  private commitSubstationAutocompleteValue(): void {
    commitReportAutocompleteValue({
      searchTerm: this.substationSearchTerm,
      form: this.filtersForm,
      controlName: 'substation_id',
      normalizeNumber: (value) => this.normalizeNumber(value),
      findOption: (label) => this.findSubstationByLabel(label),
      setSearchTerm: (value) => {
        this.substationSearchTerm = value;
      },
    });
  }

  private findSubstationByLabel(label: string): LogSheetSubstationOption | null {
    return findReportOptionByLabel(this.getVisibleGeneratorOptions(), this.getVisibleGeneratorOptions(), label);
  }

  private loadReport(preserveViewState = false): void {
    if (preserveViewState) {
      this.captureViewStateForRestore();
    } else {
      this.pendingViewStateRestore = null;
    }

    const payload = this.buildReportPayload();
    this.isLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_LOG_SHEET_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const report = this.extractReport(response);
          this.applyReport(report);
        },
        error: () => {
          if (preserveViewState) {
            this.restoreViewStateAfterRefresh();
            return;
          }

          this.applyReport(null);
        },
      });
  }

  private applyReport(report: LogSheetReportResponse | null): void {
    if (!report) {
      this.resetReportState();
      return;
    }

    this.blockColumns = report.blocks;
    this.blockGroups = this.buildBlockGroups(report.blocks);
    this.reportRowsCache = this.getDisplayRowsForCurrentMode(report.rows).map((row) => this.cloneReportRow(row));
    const shouldRestoreViewState = Boolean(this.pendingViewStateRestore);
    if (this.detailMode === 'water-level') {
      this.applyWaterLevelReportRows();
      this.applyReportMetadata(report);
      this.pendingViewStateRestore = null;
      return;
    }

    const isFlatReportMode = report.accordion_enabled === false || report.report_mode === 'interruption' || report.report_mode === 'frequency-voltage';
    if (isFlatReportMode) {
      this.applyFlatReportRows(shouldRestoreViewState);
    } else {
      this.applyHierarchyReportRows(shouldRestoreViewState);
    }
    this.applyReportMetadata(report);

    if (shouldRestoreViewState && !isFlatReportMode) {
      this.restoreViewStateAfterRefresh();
    } else {
      this.pendingViewStateRestore = null;
    }
  }

  private resetReportState(): void {
    this.blockColumns = [];
    this.blockGroups = [];
    this.rows = [];
    this.hierarchyRoots = [];
    this.reportRowsCache = [];
    this.expandedRowKeys.clear();
    this.baseLoad = 0;
    this.summary = {
      scheduled_mwh: 0,
      actual_mwh: 0,
      deviation_mwh: 0,
      row_count: 0,
    };
  }

  private applyFlatReportRows(shouldRestoreViewState: boolean): void {
    this.hierarchyRoots = [];
    const flatNodes = this.buildLinkedRowNodes(this.reportRowsCache);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultFlatExpandedRowKeys(flatNodes);
    this.rows = this.flattenHierarchyNodes(flatNodes, this.expandedRowKeys);
  }

  private applyHierarchyReportRows(shouldRestoreViewState: boolean): void {
    const hierarchyRows = this.reportRowsCache.filter((row) => !row.force_flat);
    this.hierarchyRoots = this.buildHierarchyNodes(hierarchyRows);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (!shouldRestoreViewState && this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.pruneSummaryCollapsedSourceKeys(this.hierarchyRoots, this.expandedRowKeys);
    }
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private applyReportMetadata(report: LogSheetReportResponse): void {
    this.summary = report.summary;
    this.baseLoad = report.base_load ?? 0;
    this.applyTimeIntervalMetadata(report);
    if (!this.preferencesSvc.showBsDate && report.date_label) {
      this.selectedDateDisplayLabel = report.date_label;
    }
  }

  private applyTimeIntervalMetadata(report: LogSheetReportResponse): void {
    const options = report.time_interval?.options ?? [];
    if (options.length) {
      this.timeIntervalOptions = options;
    }

    const selectedInterval = report.time_interval?.selected ?? report.filters.time_interval ?? null;
    if (selectedInterval !== null && selectedInterval !== undefined && this.filtersForm.get('time_interval')?.value !== selectedInterval) {
      this.filtersForm.patchValue({ time_interval: selectedInterval }, { emitEvent: false });
    }
  }

  private applyWaterLevelReportRows(): void {
    this.hierarchyRoots = [];
    this.expandedRowKeys.clear();
    this.rows = this.reportRowsCache.filter((row) => row.row_kind === 'data' || row.row_kind === 'power-water-level-generator');
  }

  private openEditDataModal(row: LogSheetRow, cell: LogSheetBlockCell): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      station_code: row.station_code ?? null,
      row_key: row.row_key ?? null,
      row_kind: row.row_kind ?? null,
      block_no: cell.block_no,
      block_value: getNullableNumberValue(cell.value),
      remarks: cell.remarks ?? null,
    };
    this.remarksForm.reset(buildEditDataFormValue(cell.value, cell.remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private openEnergyEditDataModal(row: LogSheetRow): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      station_code: row.station_code ?? null,
      row_key: row.row_key ?? null,
      row_kind: row.row_kind ?? null,
      block_no: 0,
      block_value: getNullableNumberValue(row.energy_mwh),
      remarks: row.energy_remarks ?? null,
      data_type: 'ENERGY',
    };
    this.remarksForm.reset(buildEditDataFormValue(row.energy_mwh, row.energy_remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private fetchPreviousBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_BLOCK_VALUE, stationId, blockNo, 'copy_previous_block_value');
  }

  private fetchPreviousDayBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_DAY_BLOCK_VALUE, stationId, blockNo, 'carry_over_previous_day_values');
  }

  private fetchPreviousValue(endpoint: string, stationId: number, blockNo: number, resetControlName: string): void {
    const context = this.pendingRemarksContext;
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: stationId,
      station_code: context?.station_code ?? null,
      row_key: context?.row_key ?? null,
      row_kind: context?.row_kind ?? null,
      block_no: blockNo,
      detail_mode: this.detailMode,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.patchPreviousValue(response),
        error: () => this.remarksForm.patchValue({ [resetControlName]: false }, { emitEvent: false }),
      });
  }

  private patchPreviousValue(response: unknown): void {
    const previous = this.extractPreviousBlockValue(response);
    if (previous?.data_value !== null && previous?.data_value !== undefined) {
      this.remarksForm.patchValue({ data_value: String(previous.data_value) }, { emitEvent: false });
    }
    if (previous?.remarks !== null && previous?.remarks !== undefined) {
      this.remarksForm.patchValue({ remarks: previous.remarks }, { emitEvent: false });
    }
  }

  private updateLocalCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    const updateCell = (row: LogSheetRow): void => {
      if (row.station_id !== stationId) {
        return;
      }

      row.blocks = row.blocks.map((cell) => {
        if (cell.block_no !== blockNo) {
          return cell;
        }

        return {
          ...cell,
          value: blockValue,
          remarks,
          source: 'logsheet_data_details',
          editor_role_name: null,
          editor_role_group: null,
        };
      });
    };

    const traverse = (nodes: LogSheetHierarchyNode[]): void => {
      for (const node of nodes) {
        updateCell(node.row);
        if (node.children.length) {
          traverse(node.children);
        }
      }
    };

    traverse(this.hierarchyRoots);
    this.updateCachedReportCellValue(stationId, blockNo, blockValue, remarks);
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private updateCachedReportCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    for (const row of this.reportRowsCache) {
      if (row.station_id !== stationId) {
        continue;
      }

      row.blocks = row.blocks.map((cell) => {
        if (cell.block_no !== blockNo) {
          return cell;
        }

        return {
          ...cell,
          value: blockValue,
          remarks,
          source: 'logsheet_data_details',
          editor_role_name: null,
          editor_role_group: null,
        };
      });
    }
  }

  private updateLocalEnergyValue(rowKey: string | null, energyValue: number | null, remarks: string | null): void {
    if (!rowKey) {
      return;
    }

    const updateRow = (row: LogSheetRow): LogSheetRow => {
      if (row.row_key !== rowKey) {
        return row;
      }

      return {
        ...row,
        energy_mwh: energyValue,
        energy_remarks: remarks,
        energy_source: 'logsheet_data_details',
      };
    };
    this.rows = this.rows.map(updateRow);
    this.reportRowsCache = this.reportRowsCache.map(updateRow);
  }

  private cloneReportRow(row: LogSheetRow): LogSheetRow {
    return {
      ...row,
      blocks: row.blocks.map((cell) => ({ ...cell })),
    };
  }

  private extractPreviousBlockValue(response: unknown): LogSheetPreviousBlockValueResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      data_value: this.normalizeNullableNumber(value['data_value']),
      remarks: this.extractStringValue(value, 'remarks'),
      previous_block_no: this.normalizeNullableNumber(value['previous_block_no']),
      source: this.extractStringValue(value, 'source'),
    };
  }

  private buildReportPayload(): Record<string, unknown> {
    const detailMode = this.detailMode === 'summary' ? 'summary' : this.detailMode;
    return {
      data_date: this.getSelectedDateValue(),
      entity_id: null,
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      show_values: this.normalizeSelectedShowValues(),
      detail_mode: detailMode,
      bs_ad_key: this.getDatePreferenceKey(),
      selected_view_tab: this.selectedViewTab,
      time_interval: this.normalizeNumber(this.filtersForm.get('time_interval')?.value) ?? 60,
    };
  }

  private getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  private normalizeSelectedShowValues(): string | null {
    const value = this.filtersForm.get('show_values')?.value;
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    return value.trim();
  }

  private captureViewStateForRestore(): void {
    const tableWrap = this.tableWrap?.nativeElement ?? null;
    this.pendingViewStateRestore = {
      expandedRowKeys: Array.from(this.expandedRowKeys),
      scrollTop: tableWrap?.scrollTop ?? 0,
      scrollLeft: tableWrap?.scrollLeft ?? 0,
    };
  }

  private restoreViewStateAfterRefresh(): void {
    const snapshot = this.pendingViewStateRestore;
    if (!snapshot || this.detailMode === 'interruption' || this.detailMode === 'frequency-voltage') {
      return;
    }

    const restore: any = () => {
      this.expandedRowKeys = new Set(snapshot.expandedRowKeys);
      this.rows = this.buildDisplayRowsWithFlatRows();

      const tableWrap = this.tableWrap?.nativeElement ?? null;
      if (tableWrap) {
        tableWrap.scrollTop = snapshot.scrollTop;
        tableWrap.scrollLeft = snapshot.scrollLeft;
      }

      this.pendingViewStateRestore = null;
      this.cdr.detectChanges();
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
      return;
    }

    setTimeout(restore, 0);
  }

  private isCurrentDateSelected(): boolean {
    if (this.preferencesSvc.showBsDate) {
      const currentBsDate = this.serverTimeSvc.getCurrentBsDateValue();
      return Boolean(currentBsDate && this.getSelectedDateValue() === currentBsDate);
    }

    return this.getSelectedDateValue() === getTodayDateValue();
  }

  private isYesterdayDateSelected(): boolean {
    const referenceDate = this.serverTimeSvc.getCurrentTime() ?? new Date();
    const yesterday = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate() - 1);
    const yesterdayValue = [
      yesterday.getFullYear(),
      String(yesterday.getMonth() + 1).padStart(2, '0'),
      String(yesterday.getDate()).padStart(2, '0'),
    ].join('-');
    return this.selectedDateAdValue === yesterdayValue;
  }

  private buildHierarchyNodes(rows: LogSheetRow[]): LogSheetHierarchyNode[] {
    const normalizedRows: LogSheetRow[] = rows.map((row) => ({
      ...row,
      row_kind: row.row_kind === 'power-small' || row.parent_row_key ? row.row_kind : 'data',
      row_level: row.row_kind === 'power-small' || row.parent_row_key ? row.row_level : 3,
      row_key: row.row_kind === 'power-small' || row.parent_row_key ? row.row_key : `data-${row.station_id}`,
      has_children: false,
    }));

    return buildGroupedReportHierarchyNodes({
      rows: normalizedRows,
      isSummaryView: this.selectedViewTab === 'summary',
      normalizeHierarchyLabel: (value) => this.normalizeHierarchyLabel(value),
      normalizeOptionalHierarchyLabel: (value) => this.normalizeOptionalHierarchyLabel(value),
      isFuelHierarchyLabel: (value) => this.isFuelHierarchyLabel(value),
      isMissingHierarchyLabel: (value) => this.isMissingHierarchyLabel(value),
      sortNodes: (nodes) => this.sortHierarchyNodesForCurrentMode(nodes),
      buildAggregateRow: (rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren) =>
        this.buildAggregateRow(rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren),
    });
  }

  private buildDisplayRowsWithFlatRows(): LogSheetRow[] {
    const flatNodes = this.sortHierarchyNodesForCurrentMode(this.buildLinkedRowNodes(this.reportRowsCache.filter((row) => row.force_flat)));
    return [
      ...this.flattenHierarchyNodes(this.hierarchyRoots, this.expandedRowKeys),
      ...this.flattenHierarchyNodes(flatNodes, this.expandedRowKeys),
    ];
  }

  private buildRowsFromCurrentState(): LogSheetRow[] {
    if (!this.hierarchyRoots.length && this.reportRowsCache.length) {
      return this.flattenHierarchyNodes(this.buildLinkedRowNodes(this.reportRowsCache), this.expandedRowKeys);
    }

    return this.buildDisplayRowsWithFlatRows();
  }

  private getDisplayRowsForCurrentMode(rows: LogSheetRow[]): LogSheetRow[] {
    if (this.detailMode !== 'generation') {
      return rows;
    }

    const visiblePowerFooterRows = new Set([
      'power-total-thermal',
      'power-thermal-generator',
      'power-kabeli-corridor',
      'power-kabeli-corridor-generator',
      'power-kathmandu-load',
      'power-spill',
    ]);
    return rows.filter((row) => !row.force_flat || visiblePowerFooterRows.has(row.row_kind ?? ''));
  }

  private getDefaultFlatExpandedRowKeys(nodes: LogSheetHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    for (const node of nodes) {
      if (node.row.has_children && node.row.row_key) {
        expandedKeys.add(node.row.row_key);
      }
    }

    return expandedKeys;
  }

  private buildLinkedRowNodes(rows: LogSheetRow[]): LogSheetHierarchyNode[] {
    return buildLinkedReportHierarchyNodes(rows, (nodes) => this.sortHierarchyNodesForCurrentMode(nodes));
  }

  private sortHierarchyNodesForCurrentMode(nodes: LogSheetHierarchyNode[]): LogSheetHierarchyNode[] {
    const orderMap = this.detailMode === 'summary' ? SUMMARY_ACCORDION_ORDER_MAP : POWER_ACCORDION_ORDER_MAP;
    return [...nodes].sort((left, right) => this.getHierarchyNodeOrder(left, orderMap) - this.getHierarchyNodeOrder(right, orderMap));
  }

  private getHierarchyNodeOrder(node: LogSheetHierarchyNode, orderMap: Map<string, number>): number {
    const key = this.getHierarchyNodeOrderKey(node.row);
    return orderMap.get(key) ?? orderMap.size + 100;
  }

  private getHierarchyNodeOrderKey(row: LogSheetRow): string {
    return getReportHierarchyNodeOrderKey(row);
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: LogSheetRow[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren = false,
  ): LogSheetRow {
    const blocks = this.blockColumns.map((block) => {
      const total = rows.reduce((sum, row) => {
        const cell = row.blocks.find((item) => item.block_no === block.block_no);
        return sum + (cell?.value ?? 0);
      }, 0);

      return {
        block_no: block.block_no,
        label: block.label,
        period: block.period,
        value: Number(total.toFixed(5)),
        remarks: null,
        source: null,
        editable_id: null,
        editor_role_name: null,
        editor_role_group: null,
      };
    });

    const scheduled_mwh = rows.reduce((sum, row) => sum + row.scheduled_mwh, 0);
    const actual_mwh = rows.reduce((sum, row) => sum + row.actual_mwh, 0);
    const deviation_mwh = rows.reduce((sum, row) => sum + row.deviation_mwh, 0);
    const energy_mwh = rows.reduce((sum, row) => sum + (row.energy_mwh ?? 0), 0);
    const installed_capacity = rows.reduce((sum, row) => sum + (row.installed_capacity ?? 0), 0);
    const installedCapacityLabel = this.formatNumber(Number(installed_capacity.toFixed(5)));

    return {
      station_id: 0,
      station_name: `${label} (${installedCapacityLabel})`,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      blocks,
      scheduled_mwh: Number(scheduled_mwh.toFixed(5)),
      actual_mwh: Number(actual_mwh.toFixed(5)),
      deviation_mwh: Number(deviation_mwh.toFixed(5)),
      energy_mwh: Number(energy_mwh.toFixed(5)),
      energy_source: null,
      energy_remarks: null,
      energy_editable_id: null,
      installed_capacity: Number(installed_capacity.toFixed(5)),
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
    };
  }

  private getRowBlockMaxValue(row: LogSheetRow): number | null {
    return getReportRowBlockExtremeValue(row, 'max');
  }

  private getRowBlockMinValue(row: LogSheetRow): number | null {
    return getReportRowBlockExtremeValue(row, 'min');
  }

  private flattenHierarchyNodes(nodes: LogSheetHierarchyNode[], expandedKeys: Set<string>): LogSheetRow[] {
    return flattenReportHierarchyNodes(
      nodes,
      expandedKeys,
      (node) => this.selectedViewTab === 'summary' && Boolean(node.row.force_expand_children),
    );
  }

  private getDefaultExpandedRowKeys(nodes: LogSheetHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    for (const node of nodes) {
      if (this.selectedViewTab === 'summary') {
        this.addSummaryExpandedRowKey(node, expandedKeys);
        continue;
      }

      if (!node.row.has_children || !node.row.row_key) {
        continue;
      }

      if ((node.row.row_level ?? 0) <= 1) {
        expandedKeys.add(node.row.row_key);
      }

      for (const child of node.children) {
        if (child.row.row_key && this.shouldExpandPowerModeChild(child)) {
          expandedKeys.add(child.row.row_key);
        }
      }
    }

    return expandedKeys;
  }

  private addSummaryExpandedRowKey(node: LogSheetHierarchyNode, expandedKeys: Set<string>): void {
    if (!node.row.has_children || !node.row.row_key) {
      return;
    }

    if (node.row.row_kind === 'sector-total') {
      expandedKeys.add(node.row.row_key);
      return;
    }

    if (node.row.row_kind === 'source-total' && node.row.force_expand_children) {
      expandedKeys.add(node.row.row_key);
    }
  }

  private shouldExpandPowerModeChild(child: LogSheetHierarchyNode): boolean {
    return Boolean(
      child.row.has_children &&
      child.row.row_key &&
      (child.row.row_level ?? 0) <= 1 &&
      this.hasSubcategoryChildren(child),
    );
  }

  private hasSubcategoryChildren(node: LogSheetHierarchyNode): boolean {
    return node.children.some((grandchild) => grandchild.row.row_kind === 'subcat-total');
  }

  private pruneSummaryCollapsedSourceKeys(nodes: LogSheetHierarchyNode[], expandedKeys: Set<string>): Set<string> {
    const prunedKeys = new Set(expandedKeys);

    const visit = (node: LogSheetHierarchyNode): void => {
      if (
        node.row.row_kind === 'source-total' &&
        node.row.row_key &&
        node.children.length > 0 &&
        node.children.every((child) => !child.row.has_children)
      ) {
        prunedKeys.delete(node.row.row_key);
      }

      for (const child of node.children) {
        visit(child);
      }
    };

    for (const node of nodes) {
      visit(node);
    }

    return prunedKeys;
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate || 'Unassigned';
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate && !this.isMissingHierarchyLabel(candidate) ? candidate : null;
  }

  private isFuelHierarchyLabel(value: string | null | undefined): boolean {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return false;
    }

    return !this.isMissingHierarchyLabel(normalized);
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return candidate === '' || candidate === 'na' || candidate === 'n/a' || candidate === 'null' || candidate === '-';
  }

  private buildBlockGroups(blocks: LogSheetBlockDefinition[]): LogSheetBlockGroup[] {
    return buildReportBlockGroups(blocks, (period) => this.getPeriodTitle(period));
  }

  private getPeriodTitle(period: LogSheetBlockDefinition['period']): string {
    switch (period) {
      case 'morning':
        return 'Morning';
      case 'day':
        return 'Day';
      case 'night':
        return 'Night';
      default:
        return 'Blocks';
    }
  }

  private resolveBlockPeriod(period: string | null | undefined, label: string | null | undefined): LogSheetBlockDefinition['period'] {
    const normalizedPeriod = typeof period === 'string' ? period.trim().toLowerCase() : '';
    if (normalizedPeriod === 'morning' || normalizedPeriod === 'day' || normalizedPeriod === 'night') {
      return normalizedPeriod;
    }

    if (typeof label === 'string' && label.trim()) {
      return resolvePeriodForBlockLabel(label.trim());
    }

    return 'morning';
  }

  private extractReport(response: unknown): LogSheetReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const blocks = this.extractBlockDefinitions(value);
    const rows = this.extractRows(value);
    const summary = this.extractSummary(value);
    const dataDate = this.extractStringValue(value, 'data_date') ?? this.getSelectedDateValue();
    const dateLabel = this.preferencesSvc.formatDateOnly(dataDate);
    const accordionEnabled = this.extractBooleanValue(value, 'accordion_enabled');

    return {
      data_date: dataDate,
      date_label: dateLabel,
      report_mode: this.extractStringValue(value, 'report_mode'),
      accordion_enabled: accordionEnabled,
      blocks,
      rows,
      summary,
      base_load: this.normalizeNumber(this.getRecordValue(value, 'base_load')) ?? 0,
      time_interval: this.extractTimeIntervalMetadata(value),
      filters: {
        entity_id: null,
        substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
        time_interval: this.normalizeNumber(this.getRecordValue(this.getRecordValue(value, 'filters'), 'time_interval')),
      },
    };
  }

  private extractTimeIntervalMetadata(value: unknown): LogSheetReportResponse['time_interval'] {
    const record = this.getRecord(this.getRecordValue(value, 'time_interval'));
    const rawOptions = this.getArrayValue(record, 'options');
    const options = rawOptions.map((item) => {
      const optionRecord = this.getRecord(item);
      return {
        value: this.normalizeNumber(optionRecord['value']) ?? 60,
        label: this.extractStringValue(optionRecord, 'label') ?? 'Hourly',
      };
    });
    return {
      selected: this.normalizeNumber(record['selected']) ?? 60,
      options: options.length ? options : [{ value: 60, label: 'Hourly' }],
    };
  }

  private extractBlockDefinitions(value: unknown): LogSheetBlockDefinition[] {
    const rows = this.getArrayValue(value, 'blocks');
    if (!rows.length) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      return {
        block_no: this.normalizeNumber(record['block_no']) ?? 0,
        label: this.extractStringValue(record, 'label') ?? '',
        period: this.resolveBlockPeriod(this.extractStringValue(record, 'period'), this.extractStringValue(record, 'label')),
      };
    });
  }

  private extractRows(value: unknown): LogSheetRow[] {
    const rows = this.getArrayValue(value, 'rows');
    if (!rows.length) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      const blocks = this.getArrayValue(record, 'blocks').map((blockItem) => {
        const blockRecord = this.getRecord(blockItem);
        return {
          block_no: this.normalizeNumber(blockRecord['block_no']) ?? 0,
          label: this.extractStringValue(blockRecord, 'label') ?? '',
          period: this.resolveBlockPeriod(this.extractStringValue(blockRecord, 'period'), this.extractStringValue(blockRecord, 'label')),
          value: this.normalizeNullableNumber(blockRecord['value']),
          remarks: this.extractStringValue(blockRecord, 'remarks'),
          source: this.extractStringValue(blockRecord, 'source') as LogSheetBlockCell['source'],
          editable_id: this.normalizeNullableNumber(blockRecord['editable_id']),
          editor_role_name: this.extractStringValue(blockRecord, 'editor_role_name'),
          editor_role_group: this.extractStringValue(blockRecord, 'editor_role_group') as LogSheetBlockCell['editor_role_group'],
        };
      });

      return {
        station_id: this.normalizeNumber(record['station_id']) ?? 0,
        station_name: this.extractStringValue(record, 'station_name') ?? '-',
        station_code: this.extractStringValue(record, 'station_code'),
        owner: this.extractStringValue(record, 'owner'),
        source_category: this.extractStringValue(record, 'source_category'),
        sub_category: this.extractStringValue(record, 'sub_category'),
        blocks,
        scheduled_mwh: this.normalizeNumber(record['scheduled_mwh']) ?? 0,
        actual_mwh: this.normalizeNumber(record['actual_mwh']) ?? 0,
        deviation_mwh: this.normalizeNumber(record['deviation_mwh']) ?? 0,
        energy_mwh: this.normalizeNullableNumber(record['energy_mwh']),
        energy_remarks: this.extractStringValue(record, 'energy_remarks'),
        energy_source: this.extractStringValue(record, 'energy_source') as LogSheetRow['energy_source'],
        energy_editable_id: this.normalizeNullableNumber(record['energy_editable_id']),
        installed_capacity: this.normalizeNullableNumber(record['installed_capacity']),
        row_key: this.extractStringValue(record, 'row_key') ?? `data-${this.normalizeNumber(record['station_id']) ?? 0}`,
        parent_row_key: this.extractStringValue(record, 'parent_row_key'),
        row_kind: this.extractStringValue(record, 'row_kind') ?? 'data',
        row_level: this.normalizeNullableNumber(record['row_level']) ?? 3,
        has_children: Boolean(record['has_children']),
        force_expand_children: Boolean(record['force_expand_children']),
        force_flat: Boolean(record['force_flat']),
        is_small_power: Boolean(record['is_small_power']),
      };
    });
  }

  private extractSummary(value: unknown): LogSheetSummary {
    const summaryRecord = this.getRecordValue(value, 'summary');
    const record = this.getRecord(summaryRecord);
    return {
      scheduled_mwh: this.normalizeNumber(record['scheduled_mwh']) ?? 0,
      actual_mwh: this.normalizeNumber(record['actual_mwh']) ?? 0,
      deviation_mwh: this.normalizeNumber(record['deviation_mwh']) ?? 0,
      row_count: this.normalizeNumber(record['row_count']) ?? 0,
    };
  }

  private extractSubstationOptions(response: unknown): LogSheetSubstationOption[] {
    const value = this.extractResponseValue(response);
    const rows = this.getArrayValue(value, 'generators');
    return rows.map((item) => {
      const record = this.getRecord(item);
      const generatorName = this.extractStringValue(record, 'generator_name') ?? this.extractStringValue(record, 'name') ?? '-';
      const scadaStationCode = this.extractStringValue(record, 'scada_station_code') ?? this.extractStringValue(record, 'station_code');
      return {
        id: this.normalizeNumber(record['id']) ?? 0,
        name: generatorName,
        station_code: scadaStationCode,
        generator_name: generatorName,
        project_unique_code: this.extractStringValue(record, 'project_unique_code'),
        scada_station_code: scadaStationCode,
        other_parameter: this.extractStringValue(record, 'other_parameter'),
      };
    });
  }

  private refreshGeneratorOptionsForDetailMode(clearInvalidSelection = true): void {
    const visibleOptions = this.getVisibleGeneratorOptions();
    this.filteredSubstationOptions = [...visibleOptions];

    if (!this.isWaterLevelDetailMode()) {
      return;
    }

    const selectedId = this.normalizeNumber(this.filtersForm.get('substation_id')?.value);
    if (selectedId === null) {
      return;
    }

    const selectedOption = visibleOptions.find((option) => option.id === selectedId) ?? null;
    if (selectedOption) {
      this.substationSearchTerm = selectedOption.name;
      return;
    }

    if (!clearInvalidSelection) {
      return;
    }

    this.substationSearchTerm = '';
    this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false });
  }

  private getVisibleGeneratorOptions(): LogSheetSubstationOption[] {
    if (!this.isWaterLevelDetailMode()) {
      return [...this.substationOptions];
    }

    return this.substationOptions.filter(
      (option) => this.hasWaterLevelParameter(option.other_parameter) && this.hasScadaCode(option),
    );
  }

  private hasWaterLevelParameter(otherParameter: string | null | undefined): boolean {
    const normalizedValue = typeof otherParameter === 'string' ? otherParameter.trim().toLowerCase() : '';
    return /water\s*level/.test(normalizedValue);
  }

  private hasScadaCode(option: LogSheetSubstationOption): boolean {
    const scadaCode = option.scada_station_code?.trim() || option.station_code?.trim();
    return Boolean(scadaCode);
  }

  private isWaterLevelDetailMode(): boolean {
    return this.detailMode === 'water-level';
  }

  private extractResponseValue(response: unknown): Record<string, unknown> | null {
    return extractReportResponseValue(response);
  }

  private getArrayValue(value: unknown, key: string): unknown[] {
    return getReportArrayValue(value, key);
  }

  private getRecordValue(value: unknown, key: string): unknown {
    return getReportRecordValue(value, key);
  }

  private getRecord(value: unknown): Record<string, unknown> {
    return getReportRecord(value);
  }

  private extractStringValue(value: unknown, key: string): string | null {
    return extractReportStringValue(value, key);
  }

  private extractBooleanValue(value: unknown, key: string): boolean | null {
    const candidate = this.getRecordValue(value, key);
    if (typeof candidate === 'boolean') {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLowerCase();
      if (normalized === 'true') {
        return true;
      }
      if (normalized === 'false') {
        return false;
      }
    }
    return null;
  }

  private normalizeNumber(value: unknown): number | null {
    return normalizeReportNumber(value);
  }

  private normalizeNullableNumber(value: unknown): number | null {
    return this.normalizeNumber(value);
  }

  private normalizeCellValue(value: string): number | null {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null;
  }

  private getSelectedDateValue(): string {
    return this.selectedDateRequestValue || this.getSelectedDateFormValue();
  }

  private getSelectedDateFormValue(): string {
    const value = this.filtersForm.get('date')?.value;
    return typeof value === 'string' && value ? value : getTodayDateValue();
  }

  protected openDatePicker(): void {
    if (this.isBsDateMode) {
      if (this.bsCalendarOpen) {
        this.closeBsDatePicker();
        return;
      }

      this.openBsDatePicker();
      return;
    }

    const input = this.hiddenDateInput?.nativeElement;
    if (!input) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.click();
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: LogSheetBsCalendarCell): void {
    if (!cell?.ad_date) {
      return;
    }

    this.closeBsDatePicker();
    this.filtersForm.patchValue({ date: cell.ad_date }, { emitEvent: true });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;

    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  private async openBsDatePicker(): Promise<void> {
    this.bsCalendarOpen = true;
    const currentDate = this.getSelectedDateValue() || this.getSelectedDateFormValue();
    const currentKey = this.getSelectedDateValue() === this.selectedDateAdValue ? 'AD' : 'BS';
    await this.loadBsCalendar(currentDate, currentKey);
  }

  private async loadBsCalendar(bsDateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    if (!bsDateValue) {
      return;
    }

    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
          ApiMethod.POST,
          {
            data_date: bsDateValue,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      const calendar = this.extractBsCalendar(response);
      if (calendar) {
        this.bsCalendarMonth = calendar;
      }
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async handleDateSelectionChange(preserveViewState = false): Promise<void> {
    const adDate = this.getSelectedDateFormValue();
    this.selectedDateAdValue = adDate;

    if (this.preferencesSvc.showBsDate) {
      await this.syncBsDateSelection(adDate);
    } else {
      this.selectedDateRequestValue = adDate;
      this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
    }

    this.loadReport(preserveViewState);
  }

  private async syncBsDateSelection(adDate: string): Promise<void> {
    if (!adDate) {
      this.selectedDateRequestValue = getTodayDateValue();
      this.selectedDateDisplayLabel = getTodayDateValue();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: adDate,
            bs_ad_key: 'BS',
          },
          false,
          { suppressToast: true },
        ),
      );
      const converted = this.extractDateConversion(response);
      if (converted) {
        this.selectedDateRequestValue = converted.selected_date || converted.bs_date;
        this.selectedDateDisplayLabel = converted.selected_date_label || converted.bs_date_label;
        return;
      }
    } catch {
      // fall back to AD value below
    }

    this.selectedDateRequestValue = adDate;
    this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
  }

  private extractDateConversion(response: unknown): LogSheetDateConversionResponse | null {
    return extractReportDateConversion(response);
  }

  private extractBsCalendar(response: unknown): LogSheetBsCalendarResponse | null {
    return extractReportBsCalendar(response);
  }

  private createCellKey(stationId: number, blockNo: number): EditableCellKey {
    return `${stationId}-${blockNo}`;
  }

  private buildExportFileName(): string {
    const dateValue = this.getSelectedDateValue();
    return `log_sheet_report_${dateValue}.xlsx`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadReportBlob(blob, fileName);
  }
}
